import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  FaEdit,
  FaArrowLeft,
  FaEye,
  FaEyeSlash,
  FaTrash,
  FaCalendarAlt,
  FaMapMarkerAlt,
  FaUsers,
  FaClock,
  FaDollarSign,
  FaStar,
  FaLanguage,
} from "react-icons/fa";

const StorytellerDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [storyteller, setStoryteller] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const fetchStoryteller = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `http://localhost:3001/api/storytellers/${id}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("adminToken")}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch storyteller");
      }

      const data = await response.json();
      setStoryteller(data.data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStoryteller();
  }, [id]);

  const handleToggleStatus = async () => {
    try {
      const action =
        storyteller.status === "published" ? "unpublish" : "publish";
      const response = await fetch(
        `http://localhost:3001/api/storytellers/${id}/${action}`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("adminToken")}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to ${action} storyteller`);
      }

      fetchStoryteller(); // Refresh the data
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDelete = async () => {
    try {
      const response = await fetch(
        `http://localhost:3001/api/storytellers/${id}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("adminToken")}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to delete storyteller");
      }

      navigate("/storytellers");
    } catch (err) {
      setError(err.message);
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case "published":
        return "bg-green-100 text-green-800";
      case "draft":
        return "bg-yellow-100 text-yellow-800";
      case "archived":
        return "bg-gray-100 text-gray-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-800"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
        {error}
      </div>
    );
  }

  if (!storyteller) {
    return (
      <div className="text-center py-8">
        <h2 className="text-2xl font-bold text-gray-900">
          Storyteller not found
        </h2>
        <Link
          to="/storytellers"
          className="text-green-600 hover:text-green-800"
        >
          Back to Storytellers
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate("/storytellers")}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-800"
          >
            <FaArrowLeft /> Back to Storytellers
          </button>
        </div>
        <div className="flex items-center space-x-2">
          <Link
            to={`/storytellers/${id}/edit`}
            className="flex items-center gap-2 px-4 py-2 bg-green-800 text-white rounded-lg hover:bg-green-900"
          >
            <FaEdit /> Edit
          </Link>
          <button
            onClick={handleToggleStatus}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg ${
              storyteller.status === "published"
                ? "bg-yellow-600 text-white hover:bg-yellow-700"
                : "bg-green-600 text-white hover:bg-green-700"
            }`}
          >
            {storyteller.status === "published" ? (
              <>
                <FaEyeSlash /> Unpublish
              </>
            ) : (
              <>
                <FaEye /> Publish
              </>
            )}
          </button>
          <button
            onClick={() => setShowDeleteModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
          >
            <FaTrash /> Delete
          </button>
        </div>
      </div>

      {/* Storyteller Info */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="h-64 bg-gradient-to-r from-green-800 to-green-600 relative">
          <img
            src={storyteller.image}
            alt={storyteller.name}
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black bg-opacity-40"></div>
          <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
            <div className="flex items-end justify-between">
              <div>
                <h1 className="text-4xl font-bold mb-2">{storyteller.name}</h1>
                <div className="flex items-center space-x-4 text-lg">
                  <span className="flex items-center gap-2">
                    <FaMapMarkerAlt /> {storyteller.location}
                  </span>
                  <span className="flex items-center gap-2">
                    <FaStar /> {storyteller.specialty}
                  </span>
                </div>
              </div>
              <div className="text-right">
                <span
                  className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${getStatusBadgeClass(
                    storyteller.status
                  )}`}
                >
                  {storyteller.status}
                </span>
                {storyteller.is_featured && (
                  <div className="mt-2 text-yellow-400">
                    <FaStar className="text-xl" />
                    <span className="ml-1 text-sm">Featured</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="p-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-6">
              {/* Story */}
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Story</h2>
                <p className="text-gray-700 leading-relaxed">
                  {storyteller.story}
                </p>
              </div>

              {/* Description */}
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                  Description
                </h2>
                <p className="text-gray-700 leading-relaxed">
                  {storyteller.description}
                </p>
              </div>

              {/* Key Highlights */}
              {storyteller.keyHighlights &&
                storyteller.keyHighlights.length > 0 && (
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900 mb-4">
                      Key Highlights
                    </h2>
                    <div className="space-y-4">
                      {storyteller.keyHighlights.map((highlight, index) => (
                        <div
                          key={index}
                          className="border-l-4 border-green-500 pl-4"
                        >
                          <h3 className="font-semibold text-gray-900">
                            {highlight.title}
                          </h3>
                          <p className="text-gray-700 mt-1">
                            {highlight.description}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

              {/* Tour Schedule */}
              {storyteller.tourSchedule &&
                storyteller.tourSchedule.length > 0 && (
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900 mb-4">
                      Tour Schedule
                    </h2>
                    <div className="space-y-6">
                      {storyteller.tourSchedule.map((day, index) => (
                        <div
                          key={index}
                          className="border border-gray-200 rounded-lg p-4"
                        >
                          <div className="flex items-center justify-between mb-3">
                            <h3 className="text-lg font-semibold text-gray-900">
                              {day.day}
                            </h3>
                            <span className="text-sm text-gray-500">
                              {day.timeline}
                            </span>
                          </div>
                          <h4 className="font-medium text-gray-800 mb-2">
                            {day.title}
                          </h4>
                          <p className="text-gray-600 mb-3">{day.theme}</p>

                          {day.activities && day.activities.length > 0 && (
                            <div className="mb-3">
                              <h5 className="font-medium text-gray-700 mb-2">
                                Activities:
                              </h5>
                              <ul className="list-disc list-inside space-y-1">
                                {day.activities.map(
                                  (activity, activityIndex) => (
                                    <li
                                      key={activityIndex}
                                      className="text-gray-600"
                                    >
                                      {activity}
                                    </li>
                                  )
                                )}
                              </ul>
                            </div>
                          )}

                          {day.meals && day.meals.length > 0 && (
                            <div>
                              <h5 className="font-medium text-gray-700 mb-2">
                                Meals:
                              </h5>
                              <ul className="space-y-1">
                                {day.meals.map((meal, mealIndex) => (
                                  <li key={mealIndex} className="text-gray-600">
                                    <span className="font-medium">
                                      {meal.time}
                                    </span>{" "}
                                    - {meal.name}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

              {/* Inclusions & Exclusions */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {storyteller.inclusions &&
                  storyteller.inclusions.length > 0 && (
                    <div>
                      <h2 className="text-xl font-bold text-gray-900 mb-4">
                        Inclusions
                      </h2>
                      <ul className="list-disc list-inside space-y-2">
                        {storyteller.inclusions.map((inclusion, index) => (
                          <li key={index} className="text-gray-700">
                            {inclusion}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                {storyteller.exclusions &&
                  storyteller.exclusions.length > 0 && (
                    <div>
                      <h2 className="text-xl font-bold text-gray-900 mb-4">
                        Exclusions
                      </h2>
                      <ul className="list-disc list-inside space-y-2">
                        {storyteller.exclusions.map((exclusion, index) => (
                          <li key={index} className="text-gray-700">
                            {exclusion}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
              </div>

              {/* Gallery */}
              {storyteller.gallery && storyteller.gallery.length > 0 && (
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">
                    Gallery
                  </h2>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {storyteller.gallery.map((image, index) => (
                      <img
                        key={index}
                        src={image}
                        alt={`Gallery ${index + 1}`}
                        className="w-full h-32 object-cover rounded-lg"
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Experience Details */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Experience Details
                </h3>
                <div className="space-y-3">
                  {storyteller.timeline && (
                    <div className="flex items-center gap-2">
                      <FaClock className="text-green-600" />
                      <span className="text-gray-700">
                        {storyteller.timeline}
                      </span>
                    </div>
                  )}
                  {storyteller.duration && (
                    <div className="flex items-center gap-2">
                      <FaCalendarAlt className="text-green-600" />
                      <span className="text-gray-700">
                        {storyteller.duration}
                      </span>
                    </div>
                  )}
                  {storyteller.minPeople && storyteller.maxPeople && (
                    <div className="flex items-center gap-2">
                      <FaUsers className="text-green-600" />
                      <span className="text-gray-700">
                        {storyteller.minPeople} - {storyteller.maxPeople} people
                      </span>
                    </div>
                  )}
                  {storyteller.minAge && (
                    <div className="flex items-center gap-2">
                      <FaUsers className="text-green-600" />
                      <span className="text-gray-700">
                        Min age: {storyteller.minAge}
                      </span>
                    </div>
                  )}
                  {storyteller.pricePerPerson && (
                    <div className="flex items-center gap-2">
                      <FaDollarSign className="text-green-600" />
                      <span className="text-gray-700">
                        ${storyteller.pricePerPerson} per person
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Languages */}
              {storyteller.languages && storyteller.languages.length > 0 && (
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">
                    Languages
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {storyteller.languages.map((language, index) => (
                      <span
                        key={index}
                        className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm"
                      >
                        {language}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Meta Information */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Meta Information
                </h3>
                <div className="space-y-2 text-sm">
                  <div>
                    <span className="font-medium text-gray-700">ID:</span>
                    <span className="text-gray-600 ml-2">
                      {storyteller.storyteller_id}
                    </span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">Created:</span>
                    <span className="text-gray-600 ml-2">
                      {formatDate(storyteller.createdAt)}
                    </span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">Updated:</span>
                    <span className="text-gray-600 ml-2">
                      {formatDate(storyteller.updatedAt)}
                    </span>
                  </div>
                  {storyteller.published_at && (
                    <div>
                      <span className="font-medium text-gray-700">
                        Published:
                      </span>
                      <span className="text-gray-600 ml-2">
                        {formatDate(storyteller.published_at)}
                      </span>
                    </div>
                  )}
                  <div>
                    <span className="font-medium text-gray-700">
                      Sort Order:
                    </span>
                    <span className="text-gray-600 ml-2">
                      {storyteller.sort_order}
                    </span>
                  </div>
                </div>
              </div>

              {/* SEO Information */}
              {(storyteller.meta_title ||
                storyteller.meta_description ||
                storyteller.meta_keywords) && (
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">
                    SEO
                  </h3>
                  <div className="space-y-2 text-sm">
                    {storyteller.meta_title && (
                      <div>
                        <span className="font-medium text-gray-700">
                          Title:
                        </span>
                        <p className="text-gray-600 mt-1">
                          {storyteller.meta_title}
                        </p>
                      </div>
                    )}
                    {storyteller.meta_description && (
                      <div>
                        <span className="font-medium text-gray-700">
                          Description:
                        </span>
                        <p className="text-gray-600 mt-1">
                          {storyteller.meta_description}
                        </p>
                      </div>
                    )}
                    {storyteller.meta_keywords && (
                      <div>
                        <span className="font-medium text-gray-700">
                          Keywords:
                        </span>
                        <p className="text-gray-600 mt-1">
                          {storyteller.meta_keywords}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3 text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
                <FaTrash className="h-6 w-6 text-red-600" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mt-4">
                Delete Storyteller
              </h3>
              <div className="mt-2 px-7 py-3">
                <p className="text-sm text-gray-500">
                  Are you sure you want to delete "{storyteller.name}"? This
                  action cannot be undone.
                </p>
              </div>
              <div className="flex justify-center space-x-4 mt-4">
                <button
                  onClick={() => setShowDeleteModal(false)}
                  className="px-4 py-2 bg-gray-300 text-gray-800 rounded-md hover:bg-gray-400"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDelete}
                  className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StorytellerDetails;
